import { Alert } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getUser } from 'apis/user';
import { getUserOwnAuctions } from 'apis/auction';
import useAppSelector from 'hooks/useAppSelector';

import { Auction } from 'models/auction/Auction';
import { User } from 'models/user/User';

import { setDocumentTitle } from 'utils';
import { sendPageviewToGa4 } from 'utils/googleAnalytics4';

import InfiniteAuctionList from 'pages/auctions/AuctionList/components/InfiniteAuctionList';
import UserDescription from './components/UserDescription';

import styles from './Profile.module.css';

function Profile() {
  const { ga4Initialized } = useAppSelector((state) => state.common);
  const { userId } = useParams();
  const [user, setUser] = useState<User | null>(null);

  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [isLoadingAuctions, setIsLoadingAuctions] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const page = useRef<number>(1);

  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  const addAuctions = (moreAuctions: Auction[]) => {
    if (moreAuctions.length === 0) {
      setHasMore(false);
    } else {
      setAuctions([...auctions, ...moreAuctions]);
      page.current += 1;
    }
  };

  const fetchNextPage = () => {
    if (isLoadingAuctions || !userId) return;
    setIsLoadingAuctions(true);

    getUserOwnAuctions(userId, page.current, 12)
      .then((data) => {
        addAuctions(data.auctions);
      })
      .catch(() => {
        setHasMore(false);
        setIsError(true);
        // TODO: Show Service Error Page
      })
      .finally(() => {
        setIsLoadingAuctions(false);
      });
  };

  useEffect(() => {
    setAuctions([]);
    setHasMore(true);
    page.current = 1;
  }, [userId]);

  useEffect(() => {
    if (userId) {
      getUser(userId)
        .then(setUser)
        .catch(() => setIsError(true))
        .finally(() => setIsLoadingUser(false));
    }
  }, [userId]);

  useEffect(() => {
    if (ga4Initialized && user) {
      sendPageviewToGa4(setDocumentTitle(`사용자 프로필 (${user.id})`));
    }
  }, [ga4Initialized, user]);

  return (
    <div className={styles.container}>
      {isError ? (
        <Alert
          message="사용자 프로필을 불러올 수 없습니다."
          description="잠시 후 다시 시도해주세요."
          type="error"
          showIcon
        />
      ) : (
        <>
          <div className="margin-bottom-24">
            <UserDescription user={user} setUser={setUser} isLoadingUser={isLoadingUser} />
          </div>
          <InfiniteAuctionList auctions={auctions} loadMore={fetchNextPage} hasMore={hasMore} />
        </>
      )}
    </div>
  );
}

export default Profile;
